import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import Page from '@/components/Page'
import BackButton from '@/components/BackButton'
import { Button } from '@/components/chadcn/Button'
import { Label } from '@/components/chadcn/Label'
import { TextInput } from '@/components/InputFields'
import Spinner from '@/components/Spinner'
import { shippingSchema, type ShippingFormData } from '@/schemas/patient'
import { useSelfQuery, useUpdateShippingInfoMutation } from '@/data/queries'

type ShippingStepProps = {
  onNext: () => void
}

const ShippingStep = ({ onNext }: ShippingStepProps) => {
  const { data: self } = useSelfQuery()
  const { mutateAsync: updateShipping, isPending } =
    useUpdateShippingInfoMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ShippingFormData>({
    mode: 'all',
    resolver: zodResolver(shippingSchema),
    values: {
      countryCode: self?.address?.countryCode ?? 'US',
      state: self?.address?.state ?? '',
      city: self?.address?.city ?? '',
      zipCode: self?.address?.zipCode ?? '',
      address1: self?.address?.address1 ?? '',
      address2: self?.address?.address2 ?? '',
    },
    resetOptions: { keepDirtyValues: true },
  })

  const onSubmit = async (formData: ShippingFormData) => {
    const patientId = self?.id ?? ''
    await updateShipping({
      patientId,
      address: {
        countryCode: formData.countryCode,
        state: formData.state,
        city: formData.city,
        zipCode: formData.zipCode,
        address1: formData.address1,
        address2: formData.address2 ?? null,
      },
    })
    onNext()
  }

  return (
    <Page showAppbar={false}>
      <div className="flex min-h-[calc(100vh-56px)] flex-col justify-between px-4 py-8">
        <div className="flex flex-col gap-6">
          <BackButton />

          <h1 className="text-2xl font-bold text-primary-contrast">
            Confirm your shipping address on record
          </h1>

          <form
            id="shipping-form"
            className="flex flex-col gap-5"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-1">
              <Label htmlFor="address1">Street address</Label>
              <TextInput
                id="address1"
                disabled={isPending}
                isError={!!errors.address1}
                {...register('address1')}
              />
              {errors.address1 && (
                <p className="text-sm text-error">{errors.address1.message}</p>
              )}
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="address2">Street address line 2</Label>
              <TextInput
                id="address2"
                disabled={isPending}
                {...register('address2')}
              />
            </div>

            <div className="flex flex-col gap-1">
              <Label htmlFor="city">City</Label>
              <TextInput
                id="city"
                disabled={isPending}
                isError={!!errors.city}
                {...register('city')}
              />
              {errors.city && (
                <p className="text-sm text-error">{errors.city.message}</p>
              )}
            </div>

            <div className="flex gap-3">
              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor="state">State</Label>
                <TextInput
                  id="state"
                  disabled={isPending}
                  isError={!!errors.state}
                  {...register('state')}
                />
                {errors.state && (
                  <p className="text-sm text-error">{errors.state.message}</p>
                )}
              </div>

              <div className="flex flex-1 flex-col gap-1">
                <Label htmlFor="zipCode">Zip code</Label>
                <TextInput
                  id="zipCode"
                  disabled={isPending}
                  isError={!!errors.zipCode}
                  {...register('zipCode')}
                />
                {errors.zipCode && (
                  <p className="text-sm text-error">{errors.zipCode.message}</p>
                )}
              </div>
            </div>

            <input type="hidden" {...register('countryCode')} />
          </form>
        </div>

        <Button
          type="submit"
          form="shipping-form"
          disabled={isPending}
          className="flex w-full gap-3 py-2 text-base"
        >
          {isPending && <Spinner />}
          Confirm Address
        </Button>
      </div>
    </Page>
  )
}

export default ShippingStep
